import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';
import { useNavigate } from 'react-router-dom';

const categoryGroups = [
  { title: 'Abbigliamento', cats: ['shoes', 'hoodies', 'tshirts', 'jackets', 'pants'] },
  { title: 'Completa il Look', cats: ['headwear', 'sets', 'underwear', 'jersey'] },
  { title: 'Accessori & Altro', cats: ['accessories', 'bags', 'belts', 'perfumes', 'other'] },
];

function getCategoryById(id: string) {
  return categories.find(c => c.id === id);
}

export default function Navbar() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileOpen]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const closeMobile = () => setIsMobileOpen(false);

  const navLinks = [
    { label: 'Home', href: '/' },
    { label: 'Guida all\'acquisto', href: '/articoli/guida-completa' },
    { label: 'Prodotti', hasDropdown: true },
    { label: 'Sicurezza', href: '/articoli/sicurezza' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8 lg:px-16">
        <div className="flex items-center justify-between h-16 md:h-[4.5rem]">
          <a href="/" className="flex items-center gap-2 group">
            <span className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 tracking-tight">
              Itaobuy<span className="text-primary-500">Spread</span>
            </span>
          </a>

          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) =>
              link.hasDropdown ? (
                <div key={link.label} className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="flex items-center gap-1.5 text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors nav-link-hover whitespace-nowrap cursor-pointer"
                  >
                    {link.label}
                    <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
                  </button>

                  {isDropdownOpen && (
                    <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-72 bg-background-50 rounded-xl shadow-lg border border-background-200 py-4 z-50">
                      <div className="max-h-[420px] overflow-y-auto px-2">
                        {categoryGroups.map((group) => (
                          <div key={group.title} className="mb-3 last:mb-0">
                            <div className="px-3 py-1 text-xs font-semibold uppercase tracking-wider text-foreground-400">
                              {group.title}
                            </div>
                            {group.cats.map((catId) => {
                              const cat = getCategoryById(catId);
                              if (!cat) return null;
                              return (
                                <a
                                  key={cat.id}
                                  href={cat.url}
                                  target="_blank"
                                  rel="noopener nofollow"
                                  className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-background-100 transition-colors group whitespace-nowrap"
                                  onClick={() => setIsDropdownOpen(false)}
                                >
                                  <span className="w-7 h-7 flex items-center justify-center rounded-md bg-background-100 group-hover:bg-primary-50 transition-colors">
                                    <i className={`${cat.icon} text-sm text-foreground-600 group-hover:text-primary-500 transition-colors`}></i>
                                  </span>
                                  <span className="text-sm text-foreground-800 group-hover:text-primary-600 transition-colors font-medium">
                                    {cat.name}
                                  </span>
                                </a>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <a
                  key={link.label}
                  href={link.href || '/'}
                  className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors nav-link-hover whitespace-nowrap"
                >
                  {link.label}
                </a>
              )
            )}

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
            >
              <span className="w-6 h-6 rounded-full bg-background-50/20 flex items-center justify-center text-xs font-bold">IS</span>
              itaobuy sheet
              <i className="ri-arrow-right-up-line text-sm"></i>
            </a>
          </div>

          <button
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
            onClick={() => setIsMobileOpen(!isMobileOpen)}
          >
            <i className={`text-xl ${isMobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {isMobileOpen && (
        <>
          <div
            className="md:hidden fixed inset-0 top-16 bg-black/30 z-40"
            onClick={closeMobile}
          />
          <div
            ref={mobileRef}
            className="md:hidden fixed top-16 left-0 right-0 bottom-0 z-50 bg-background-50 overflow-y-auto overscroll-contain"
          >
            <div className="px-4 py-3 space-y-1 pb-8">
              <a
                href="/"
                className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-background-100 transition-colors"
                onClick={closeMobile}
              >
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-50">
                  <i className="ri-home-4-line text-lg text-primary-500"></i>
                </span>
                <span className="text-sm font-semibold text-foreground-800">Home</span>
              </a>

              <a
                href="/articoli/guida-completa"
                className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-background-100 transition-colors"
                onClick={closeMobile}
              >
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-50">
                  <i className="ri-book-open-line text-lg text-accent-600"></i>
                </span>
                <span className="text-sm font-semibold text-foreground-800">Guida all'acquisto</span>
              </a>

              <a
                href="/articoli/sicurezza"
                className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-background-100 transition-colors"
                onClick={closeMobile}
              >
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-50">
                  <i className="ri-shield-check-line text-lg text-secondary-600"></i>
                </span>
                <span className="text-sm font-semibold text-foreground-800">Sicurezza</span>
              </a>

              {categoryGroups.map((group) => (
                <div key={group.title} className="pt-3">
                  <div className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-foreground-400">
                    {group.title}
                  </div>
                  {group.cats.map((catId) => {
                    const cat = getCategoryById(catId);
                    if (!cat) return null;
                    return (
                      <a
                        key={cat.id}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-background-100 transition-colors"
                        onClick={closeMobile}
                      >
                        <span className="w-7 h-7 flex items-center justify-center rounded-md bg-background-100">
                          <i className={`${cat.icon} text-sm text-foreground-600`}></i>
                        </span>
                        <span className="text-sm text-foreground-800 font-medium">{cat.name}</span>
                        <i className="ri-arrow-right-up-line text-xs text-foreground-400 ml-auto"></i>
                      </a>
                    );
                  })}
                </div>
              ))}

              <div className="pt-3 mt-2 border-t border-background-200">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all cursor-pointer"
                  onClick={closeMobile}
                >
                  itaobuy sheet
                  <i className="ri-arrow-right-up-line text-sm"></i>
                </a>
              </div>
            </div>
          </div>
        </>
      )}
    </nav>
  );
}